import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Spin, Tooltip, Typography } from 'antd';
import { LockOutlined, SafetyOutlined, UserOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, type CaptchaChallenge } from '@mall/shared';
import { useAuth } from './AuthProvider';
import { useToast } from '../../app/ToastProvider';
import { fetchCaptcha } from './api';

interface LoginFormValues {
  username: string;
  password: string;
  captcha: string;
}

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [form] = Form.useForm<LoginFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captcha, setCaptcha] = useState<CaptchaChallenge | null>(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const refreshCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try {
      const next = await fetchCaptcha();
      setCaptcha(next);
      form.setFieldValue('captcha', '');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : '验证码加载失败，请刷新';
      setError(message);
    } finally {
      setCaptchaLoading(false);
    }
  }, [form]);

  useEffect(() => {
    void refreshCaptcha();
  }, [refreshCaptcha]);

  const onFinish = async (values: LoginFormValues) => {
    if (!captcha) {
      setError('验证码尚未加载，请稍候');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const session = await login({
        username: values.username,
        password: values.password,
        captchaToken: captcha.captchaToken,
        captchaCode: values.captcha.trim(),
      });
      toast.success(`欢迎回来，${session.username}`);
      const target = (location.state as LocationState | null)?.from || '/';
      navigate(target, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // OperatorIpAllowlistFilter typically returns 403 + custom message —
        // surface the message inline so it doesn't disappear with the toast.
        setError(err.message || '登录失败');
        toast.error(err.message || '登录失败');
      } else {
        setError('登录失败，请稍后重试');
      }
      void refreshCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mall-auth-page">
      <section className="mall-auth-hero">
        <div className="mall-auth-eyebrow">Operator Console</div>
        <Typography.Title className="mall-auth-title">
          跨租户运营、治理和商品价格管理
        </Typography.Title>
        <Typography.Paragraph className="mall-auth-description">
          面向平台运营团队，集中管理租户、全局分类、品牌和商品上下架策略。
        </Typography.Paragraph>
        <div className="mall-auth-points">
          <div className="mall-auth-point">
            <strong>租户治理</strong>
            <span>创建、启停和重置商户账号</span>
          </div>
          <div className="mall-auth-point">
            <strong>全局目录</strong>
            <span>统一管理分类和品牌视图</span>
          </div>
          <div className="mall-auth-point">
            <strong>价格管控</strong>
            <span>批量调整售价和下架商品</span>
          </div>
        </div>
      </section>
      <Card className="mall-auth-card">
        <Typography.Title level={3} style={{ marginTop: 0, marginBottom: 6 }}>
          运营登录
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
          使用超级管理员账号进入跨租户运营平台。
        </Typography.Paragraph>
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setError(null)}
          />
        )}
        <Form<LoginFormValues>
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          initialValues={{ username: '', password: '', captcha: '' }}
        >
          <Form.Item
            label="超级管理员账号"
            name="username"
            rules={[{ required: true, message: '请输入账号' }]}
          >
            <Input prefix={<UserOutlined />} size="large" autoFocus />
          </Form.Item>
          <Form.Item
            label="密码"
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} size="large" />
          </Form.Item>
          <Form.Item label="验证码" required style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <Form.Item
                name="captcha"
                noStyle
                rules={[
                  { required: true, message: '请输入验证码' },
                  { len: 4, message: '验证码为 4 位字符' },
                ]}
              >
                <Input
                  prefix={<SafetyOutlined />}
                  size="large"
                  placeholder="不区分大小写"
                  maxLength={4}
                  autoComplete="off"
                  style={{ flex: 1 }}
                />
              </Form.Item>
              <CaptchaPreview
                challenge={captcha}
                loading={captchaLoading}
                onRefresh={refreshCaptcha}
              />
            </div>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={submitting}
              disabled={!captcha}
            >
              登录
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

interface CaptchaPreviewProps {
  challenge: CaptchaChallenge | null;
  loading: boolean;
  onRefresh: () => void;
}

function CaptchaPreview({ challenge, loading, onRefresh }: CaptchaPreviewProps) {
  return (
    <Tooltip title="点击刷新验证码">
      <button
        type="button"
        onClick={onRefresh}
        aria-label="刷新验证码"
        disabled={loading}
        style={{
          width: 130,
          height: 40,
          padding: 0,
          border: '1px solid var(--mall-color-border)',
          borderRadius: 6,
          background: '#f5f6f8',
          cursor: loading ? 'wait' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flex: '0 0 130px',
        }}
      >
        {loading || !challenge ? (
          <Spin size="small" />
        ) : (
          <img
            src={challenge.image}
            alt="验证码"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
      </button>
    </Tooltip>
  );
}
